package features.attendance.helpers

import AppState
import features.attendance.domain.AttendanceRecord
import features.members.domain.Member
import features.shifts.domain.ShiftTemplate
import kotlinx.browser.window
import kotlin.js.Date

object AttendanceHelpers {
    
    /**
     * Get the current day of the month (1-31)
     */
    fun getCurrentDay(): Int {
        val now = Date()
        return now.getDate()
    }
    
    /**
     * Get today's date in YYYY-MM-DD format
     */
    fun getTodayDate(): String {
        val now = Date()
        val year = now.getFullYear()
        val month = (now.getMonth() + 1).toString().padStart(2, '0')
        val day = now.getDate().toString().padStart(2, '0')
        return "$year-$month-$day"
    }
    
    /**
     * Get the current month (1-12)
     */
    fun getCurrentMonth(): Int {
        val now = Date()
        return now.getMonth() + 1 // JavaScript months are 0-indexed
    }
    
    /**
     * Get the current year
     */
    fun getCurrentYear(): Int {
        val now = Date()
        return now.getFullYear()
    }
    
    /**
     * Get user's shift template for today
     * Returns null if no shift is assigned for today
     */
    fun getUserShiftForToday(userId: String, teamId: String): ShiftTemplate? {
        val schedule = AppState.schedules[teamId] ?: return null
        val currentMonth = getCurrentMonth()
        val currentYear = getCurrentYear()
        
        // Check if schedule matches current month/year
        if (schedule.month != currentMonth || schedule.year != currentYear) {
            return null
        }
        
        val currentDay = getCurrentDay()
        val shiftCode = schedule.assignments[userId]?.get(currentDay) ?: return null
        
        // Find the shift template by code
        val shiftTemplates = AppState.shiftTemplates[teamId] ?: return null
        return shiftTemplates.find { it.code == shiftCode }
    }
    
    /**
     * Get work hours for a user (shift or default)
     * Returns Pair(startTime, endTime)
     */
    fun getWorkHoursForUser(userId: String, teamId: String): Pair<String, String> {
        // First, try to get shift for today
        val shift = getUserShiftForToday(userId, teamId)
        if (shift != null && shift.code != "OFF") {
            return Pair(shift.startTime, shift.endTime)
        }
        
        // If no shift or shift is OFF, use team's default work hours
        val team = AppState.teams.find { it.id == teamId }
        return if (team != null) {
            Pair(team.defaultStartTime, team.defaultEndTime)
        } else {
            Pair("08:00", "17:00") // Fallback default
        }
    }
    
    /**
     * Get today's attendance record for a user
     */
    fun getTodayAttendance(userId: String, teamId: String): AttendanceRecord? {
        val todayDate = getTodayDate()
        console.log("📅 getTodayAttendance - Today: $todayDate, User: $userId, Team: $teamId")
        
        val records = AppState.attendance[teamId]
        console.log("📊 AppState.attendance[$teamId] size: ${records?.size ?: 0}")
        
        if (records != null && records.isNotEmpty()) {
            console.log("📋 All attendance records for team $teamId:")
            records.forEach { rec ->
                console.log("   - userId=${rec.userId}, date=${rec.date}, checkIn=${rec.checkIn}, checkOut=${rec.checkOut}")
            }
        } else {
            console.log("⚠️ No attendance records found in AppState for team $teamId")
        }
        
        val found = records?.find { it.userId == userId && it.date == todayDate }
        console.log("🔍 Found today's record: ${if (found != null) "YES (checkIn=${found.checkIn}, checkOut=${found.checkOut})" else "NO"}")
        
        return found
    }
    
    /**
     * Get attendance status for a user today
     * Returns: "not_clocked_in", "clocked_in", "clocked_out"
     */
    fun getAttendanceStatus(userId: String, teamId: String): String {
        console.log("🔍 getAttendanceStatus called - User: $userId, Team: $teamId")
        val todayRecord = getTodayAttendance(userId, teamId)
        
        val status = when {
            todayRecord == null -> "not_clocked_in"
            todayRecord.checkOut != "-" -> "clocked_out"
            todayRecord.checkIn != "-" -> "clocked_in"
            else -> "not_clocked_in"
        }
        
        console.log("✅ Attendance status result: $status")
        return status
    }
    
    /**
     * Get all users scheduled for today (for admin view)
     * Returns list of members who have a shift or should use default hours
     */
    fun getAllUsersScheduledToday(teamId: String): List<Member> {
        val members = AppState.members[teamId] ?: return emptyList()
        val schedule = AppState.schedules[teamId]
        val currentDay = getCurrentDay()
        val currentMonth = getCurrentMonth()
        val currentYear = getCurrentYear()
        
        // If no schedule exists or schedule is for different month, return all members (they'll use default hours)
        if (schedule == null || schedule.month != currentMonth || schedule.year != currentYear) {
            return members
        }
        
        // Return members who have a shift assigned for today OR all members if we want to show everyone
        return members.filter { member ->
            val shiftCode = schedule.assignments[member.id]?.get(currentDay)
            // Include if has shift and it's not OFF, or if no shift (will use default hours)
            shiftCode == null || shiftCode != "OFF"
        }
    }
    
    /**
     * Check if user is late based on their shift/default hours
     * Returns true if clocked in after start time
     */
    fun isLate(userId: String, teamId: String): Boolean {
        val todayRecord = getTodayAttendance(userId, teamId) ?: return false
        if (todayRecord.checkIn == "-") return false
        
        val (startTime, _) = getWorkHoursForUser(userId, teamId)
        
        // Simple time comparison (assumes HH:MM format)
        return todayRecord.checkIn > startTime
    }
    
    /**
     * Get user's role in a team
     */
    fun getUserRole(userId: String, teamId: String): String {
        val team = AppState.teams.find { it.id == teamId }
        if (team?.ownerId == userId) return "owner"
        
        val member = AppState.members[teamId]?.find { it.id == userId }
        return member?.role ?: "member"
    }
    
    /**
     * Check if user is admin or owner
     */
    fun isAdminOrOwner(userId: String, teamId: String): Boolean {
        val role = getUserRole(userId, teamId)
        return role == "owner" || role == "admin"
    }
    
    /**
     * Get filtered attendance records based on user role
     * Admin/Owner can see all records, members can only see their own
     */
    fun getFilteredAttendanceRecords(teamId: String, userId: String): List<AttendanceRecord> {
        console.log("🔍 log-attendance: getFilteredAttendanceRecords() called")
        console.log("🔍 log-attendance: Team ID:", teamId)
        console.log("🔍 log-attendance: User ID:", userId)
        
        val allRecords = AppState.attendance[teamId] ?: emptyList()
        console.log("🔍 log-attendance: All records from AppState:", allRecords.size)
        
        val userRole = getUserRole(userId, teamId)
        console.log("🔍 log-attendance: User role:", userRole)
        
        val filteredRecords = if (userRole == "owner" || userRole == "admin") {
            console.log("🔍 log-attendance: User is admin/owner - returning all records")
            // Admin/Owner can see all attendance records
            allRecords
        } else {
            console.log("🔍 log-attendance: User is member - filtering by userId")
            // Regular members can only see their own records
            allRecords.filter { it.userId == userId }
        }
        
        console.log("🔍 log-attendance: Filtered records count:", filteredRecords.size)
        return filteredRecords
    }
}
